/**
 * The two order statistics the scoring library runs on.
 *
 * Median rather than mean throughout: it rejects vibrato extremes and residual tracker glitches
 * without needing a vibrato model at all, which is what lets §5.6 report stability instead of
 * scoring it.
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.2, §5.6
 */

/** Linear-interpolated quantile of an already-ascending array. Returns 0 for an empty input. */
export const quantile = (sorted: number[], q: number) => {
  if (sorted.length === 0) {
    return 0
  }
  const position = (sorted.length - 1) * q
  const lower = Math.floor(position)
  const upper = Math.ceil(position)
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower)
}

/** Median of an unsorted array. Returns 0 for an empty input. */
export const median = (values: number[]) => quantile([...values].sort((a, b) => a - b), 0.5)

/**
 * Mean of the values between Q1 and Q3.
 *
 * The median and the mean each fail one half of what a sung note needs. A median rejects a
 * tracker glitch but cannot average an oscillation to its centre: over one vibrato period
 * sampled at 8.5 frames it still sits ~4 c off, and lengthening the note does not help, because
 * the error is the sampling grid rather than the truncation. A mean averages the oscillation
 * exactly but one octave-error frame moves it 149 c.
 *
 * Trimming to the interquartile range and then averaging does both: symmetric trimming leaves a
 * symmetric distribution's centre where it was, and the outliers are outside the range being
 * averaged. Measured on a 5.5 Hz vibrato over whole periods: 0.99 c worst-case bias against the
 * median's 3.77 c, and 19 c under a single injected octave error against the median's 26 c.
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.2
 */
export const interquartileMean = (values: number[]) => {
  if (values.length === 0) {
    return 0
  }
  const sorted = [...values].sort((a, b) => a - b)
  const low = quantile(sorted, 0.25)
  const high = quantile(sorted, 0.75)
  const inner = sorted.filter(value => value >= low && value <= high)
  return inner.length === 0 ? quantile(sorted, 0.5) : inner.reduce((total, v) => total + v, 0) / inner.length
}
